import { z } from 'zod';
import { defineContract } from './contract';

/**
 * Public content (module `content-publishing`, web-presence §2). These schemas describe what an
 * anonymous reader may see: published items only, public fields only. They are STRICT, so a field
 * the server adds by mistake (id, tenant_id, status, a future time) fails the contract test.
 *
 * `publishedAt` is always in the past by construction (the database clock decides visibility).
 * `updatedAt` is never earlier than `publishedAt` and never in the future.
 */
const slugSchema = z.string().min(1);
const localeSchema = z.string().min(2);
const isoSchema = z.iso.datetime();

export const publicContentCoverSchema = z.strictObject({
  url: z.string().min(1),
  /** Both sizes are present or both are absent; absent means unknown (omit og:image sizes). */
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export type PublicContentCover = z.infer<typeof publicContentCoverSchema>;

const publicContentFields = {
  slug: slugSchema,
  locale: localeSchema,
  title: z.string(),
  summary: z.string().nullable(),
  cover: publicContentCoverSchema.nullable(),
  publishedAt: isoSchema,
  updatedAt: isoSchema,
};

/** A list row: no body. */
export const publicContentSummarySchema = z.strictObject(publicContentFields);
export type PublicContentSummary = z.infer<typeof publicContentSummarySchema>;

/** The detail page: the summary fields plus the markdown body. */
export const publicContentDetailSchema = z.strictObject({
  ...publicContentFields,
  body: z.string(),
});
export type PublicContentDetail = z.infer<typeof publicContentDetailSchema>;

/** Cursor pagination (a feed): `nextCursor` is null on the last page. */
export const publicContentPageSchema = z.strictObject({
  data: z.array(publicContentSummarySchema),
  meta: z.strictObject({ nextCursor: z.string().nullable() }),
});
export type PublicContentPage = z.infer<typeof publicContentPageSchema>;

export const publicContentSitemapSchema = z.strictObject({
  data: z.array(z.strictObject({ slug: slugSchema, locale: localeSchema, lastmod: isoSchema })),
});
export type PublicContentSitemap = z.infer<typeof publicContentSitemapSchema>;

export const PUBLIC_CONTENT_MAX_LIMIT = 100;

const localeQuery = z.string().min(2).max(35);

export const listPublicContentQuery = z.object({
  locale: localeQuery,
  cursor: z.string().min(1).max(200).optional(),
  limit: z.coerce.number().int().min(1).max(PUBLIC_CONTENT_MAX_LIMIT).default(20),
});
export type ListPublicContentQuery = z.input<typeof listPublicContentQuery>;

export const publicContentLocaleQuery = z.object({ locale: localeQuery });
export type PublicContentLocaleQuery = z.input<typeof publicContentLocaleQuery>;

export const contentContracts = {
  list: defineContract({
    method: 'GET',
    path: '/public/content/:collection',
    query: listPublicContentQuery,
    response: publicContentPageSchema,
  }),
  sitemap: defineContract({
    method: 'GET',
    path: '/public/content/:collection/sitemap',
    query: publicContentLocaleQuery,
    response: publicContentSitemapSchema,
  }),
  get: defineContract({
    method: 'GET',
    path: '/public/content/:collection/:slug',
    query: publicContentLocaleQuery,
    response: publicContentDetailSchema,
  }),
};
